<!--
    @change="updateValue($event.target.checked)"
-->
<template>
    <!--
        The 5th data type ("DISEASES with Category and Checkbox Options") is a completely different
        patient experience — category tiles + checkbox grids instead of a type-ahead dropdown — but
        the SAME stored [{text, value}] array under teamioODColumn 'Diseases'. It is branched off
        here so the four legacy data types below stay byte-for-byte what they were.
    -->
    <DiseasesCategorizedField
        v-if="isCategorized"
        :control="control"
        :value="selectedValues"
        :language="language"
        :view-submission-mode="viewSubmissionMode"
        :builder-mode="builderMode"
        @change="onCategorizedChange"
    />

    <div v-else-if="control.selectedOptions && control.selectedOptions.length > 0">
        <!--
        <div v-for="(option, index) in radioOptions" :key="index">
        <input              
            :id="option.value"      
            :name="control.uniqueId"        
            :value="option.value"    
            type="checkbox"
            @change="test($event.target)"
        />
        <label class="ml-2" v-bind:for="option.value">{{option.text}}</label>
        </div>
        -->

        <!--
            REFERRALS ("How did you hear about us") is SINGLE-select and has NO "None":
            a patient always came from somewhere, and Open Dental's refattach holds one
            primary referral source. The stored value is still a ONE-ITEM array so the
            submission view, the PDF, alert highlighting and the backend all keep
            consuming the same [{text, value}] shape as the other three data types.
        -->
        <multiselect
              v-if="!viewSubmissionMode && isReferrals"
              v-model="singleValue"
              :options="control.selectedOptions"
              :multiple="false"
              :allow-empty="true"
              :taggable="false"
              :show-labels="false"
              label="text"
              track-by="value"
              placeholder="Select an option"
            />

        <multiselect
              v-else-if="!viewSubmissionMode"
              v-model="selectedValues"
              :options="optionsWithNone"
              :multiple="true"
              :taggable="false"
              :show-labels="false"
              label="text"
              track-by="value"
              aria-placeholder
              @select="update($event)"
              @remove="update($event)"
            />

        <p v-else>
            <span v-for="(item, counter) in selectedValues" :key="counter">
                {{ item.text }}
            </span>
        </p>
    </div>
</template>

<script>
    import BaseControlSkeleton from "../../skeletons/controls/BaseControlSkeleton";
    import ListItem from "../../libraries/list-item.class";
    import Multiselect from "vue-multiselect";
    import DiseasesCategorizedField from "./DiseasesCategorizedField.vue";
    import { isCategorizedDiseases } from "../../libraries/od-categorized-diseases";

    export default {
        name: "OpenDentalDataControl",
        extends: BaseControlSkeleton,
        components: { Multiselect, DiseasesCategorizedField },
        props: {
            siteID: String,            
            viewSubmissionMode: Boolean
        },

        data: () => ({            
            apiURL: `${process.env.VUE_APP_TEAMIO_API_URL}/get-od-data`,            
            selectedValues: [],
            textKey: '',
            valueKey: ''
        }),

        watch: {
            control: {
                deep: true,
                handler(controlObj) {
                    //console.log(`in opendentaldata control watch, handler called with val: ${JSON.stringify(controlObj)}`);
                    /*
                    if(controlObj.dataType)
                        this.retrieveRadioOptionsList();
                    */                   
                },
            },
            // `immediate` so a value already present at mount is applied. OD controls
            // inside conditionally-visible sections mount AFTER their data loads, so
            // the value never "changes" and a non-immediate watcher never fires,
            // leaving selectedValues empty -> blank in the submission view/PDF.
            // (Same root cause as the DatePicker "birth date not staying" bug, 2026-07-01.)
            value: {
                immediate: true,
                handler(val) {
                    this.setValue(val);
                }
            }
        },

        created() {    
            //this.retrieveRadioOptionsList();        
        },

        mounted() {            
        },

        beforeDestroy() {            
        },

        computed: {
            builderMode() {
                if(this.$parent.readOnly || this.$parent.readOnly === false)
                    return false;
                else
                    return true;
            },

            /**
             * REFERRALS is the only single-select data type. Compared case-insensitively
             * because ConfigData written by older tooling can carry a lower-cased dataType.
             */
            isReferrals() {
                return String(this.control && this.control.dataType || '').toUpperCase() === 'REFERRALS';
            },

            /**
             * DISEASES_CATEGORIZED — the category-tile/checkbox variant of DISEASES. The rule lives
             * in the import-free library so the node tests exercise the shipped code.
             */
            isCategorized() {
                return isCategorizedDiseases(this.control);
            },

            /**
             * Single-select adapter over the shared array storage.
             * get: the one selected item (or null so vue-multiselect renders its placeholder)
             * set: rewraps into a one-item array and emits it, exactly like the multi path.
             */
            singleValue: {
                get() {
                    return (Array.isArray(this.selectedValues) && this.selectedValues[0]) || null;
                },
                set(val) {
                    this.selectedValues = val ? [val] : [];
                    this.updateValue(this.selectedValues);
                }
            },

            /**
             * Returns the options with "None" always at the top.
             * If "None" exists in the list from OD, move it to top.
             * If "None" doesn't exist, add it at the top.
             */
            optionsWithNone() {
                if (!this.control.selectedOptions || this.control.selectedOptions.length === 0) {
                    return [];
                }

                // Find if "None" already exists (case-insensitive)
                const existingNoneIndex = this.control.selectedOptions.findIndex(
                    option => option.text && option.text.toLowerCase() === 'none'
                );

                if (existingNoneIndex !== -1) {
                    // "None" exists - move it to the top
                    const noneOption = this.control.selectedOptions[existingNoneIndex];
                    const otherOptions = this.control.selectedOptions.filter((_, index) => index !== existingNoneIndex);
                    return [noneOption, ...otherOptions];
                }

                // "None" doesn't exist - add it at the top
                const noneOption = { text: 'None', value: 'none' };
                return [noneOption, ...this.control.selectedOptions];
            }
        },

        methods: {

            update(e) {                
                this.$nextTick(() => {
                    //console.dir(e);
                    //console.dir(this.selectedValues);
                    this.updateValue(this.selectedValues);
                });
            },

            /**
             * The categorized field emits the finished stored array (the None sentinel, the ticked
             * items and any preserved charted problems). Store it exactly as handed over — every
             * rule about dedupe / None / preservation already ran in the library.
             *
             * @param {Array} val
             */
            onCategorizedChange(val) {
                this.selectedValues = Array.isArray(val) ? val : [];
                this.updateValue(this.selectedValues);
            },

            setValue(val) {
                // Coerce to an array: guards the immediate mount-time call when the
                // value is undefined (before data loads) so the multiselect / v-for
                // never sees a non-array, and an unset field renders blank.
                this.selectedValues = Array.isArray(val) ? val : [];
            },

            /*
            I don't need this below because on run time, we're only showing options that they selected in config
            retrieveRadioOptionsList() {
                if(this.control.dataType && this.siteID) {
                    let payload = {
                        siteID: this.siteID,
                        dataType: this.control.dataType
                    }

                    //console.log(`CheckboxAPI Payload`, payload);

                    fetch(this.apiURL, {
                        method: "POST",
                        body: JSON.stringify(payload)
                    }).then(result => result.json())
                    .then(this.afterRestAPICallDataSuccessfully)
                    .catch(this.restAPICallErrorHandling);
                }
            },
            */

            /**
             * [FOR-RestAPI-Dropdown][EVENT]
             * Appending data after success
             * @param {[]} result
             */
             afterRestAPICallDataSuccessfully(result) {
                if (!Array.isArray(result)) {
                    throw new TypeError(`[CHECKBOXAPI-${this.control.name}] Wrong API-data format.`);
                }

                if(!this.control.dataType || this.control.dataType === '') {
                    throw new TypeError(`[CHECKBOXAPI-${this.control.name}] Missing OD Data Type. Please reconfigure`);
                }

                // clear list
                this.radioOptions = [];                

                switch(this.control.dataType.toUpperCase()) {
                    case 'DISEASES' : this.textKey = 'DiseaseName'; this.valueKey = 'DiseaseDefNum'; break;
                    case 'DISEASES_CATEGORIZED': this.textKey = 'DiseaseName'; this.valueKey = 'DiseaseDefNum'; break;
                    case 'ALLERGIES': this.textKey = 'Description'; this.valueKey = 'AllergyDefNum'; break;
                    case 'MEDICATIONS': this.textKey = 'MedName'; this.valueKey = 'MedicationNum'; break;
                    case 'REFERRALS': this.textKey = 'ReferralName'; this.valueKey = 'ReferralNum'; break;
                }

                /*
                    Might have to do next tick thing coz it wasn't loading during design time
                    this.$nextTick(() => somefunction());
                */

                // traversal all list and add it into the list
                result.forEach(optionObj => {                    
                    this.radioOptions.push(
                        new ListItem(
                            optionObj[this.valueKey],
                            optionObj[this.textKey]
                        )
                    );
                });
            },

            /**
             * [FOR-RestAPI-Dropdown][EVENT]
             * Show error for dropdown.
             */
             restAPICallErrorHandling(e) {
                console.error(`[CHECKBOXAPI-Control-${this.control.uniqueId}] Request API to get data failed.`, e);
            },


        },
    }
</script>